// DEM-45 — Engineering Dashboard PI agent panel. Right-side controlled
// component, mirrors the VoicePanel pattern already in Layout.tsx (width
// animates 0 → 420px when open). Always mounted so the assistant-ui
// state survives close/open cycles — the panel just hides via `inert`.
//
// The chat body is assistant-ui (@assistant-ui/react) driven by a
// pi-agent-core Agent that talks to the Engineering Dashboard MCP. Auth
// flows through a global fetch interceptor that swaps in a fresh WorkOS
// access token — see agent-runtime.ts.
import { useCallback } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { MessageCircle, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { MyJarvisRuntimeProvider } from "./MyJarvisRuntimeProvider";
import { Thread } from "./thread";

/**
 * Small toggle chip that opens the agent panel. Sized to sit next to
 * VoicePanelToggle in the top-right cluster.
 */
export function AgentPanelToggle({ onClick }: { onClick: () => void }) {
	return (
		<button
			type="button"
			aria-label="Open PI agent"
			onClick={onClick}
			title="PI · Engineering"
			className="relative flex h-9 items-center gap-1.5 rounded-lg border bg-background px-2.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
		>
			<MessageCircle className="h-[18px] w-[18px]" />
			<span className="text-xs font-medium">PI</span>
		</button>
	);
}

/**
 * The right-side agent panel. Controlled by `open` from Layout. Always
 * mounted so the chat keeps its state between open/close cycles — only
 * the width animates. Same architecture as VoicePanel.
 *
 * `fullScreen` switches to a fill-the-container mode used by the mobile
 * sheet host (DEM-48) — drops the width animation + fixed 420px chrome,
 * and hides the in-panel close button (the sheet has its own).
 */
export function AgentPanel({
	open,
	onClose,
	fullScreen = false,
}: {
	open: boolean;
	onClose: () => void;
	fullScreen?: boolean;
}) {
	const { user, getAccessToken } = useAuth();

	const getToken = useCallback(
		async () => (await getAccessToken()) || undefined,
		[getAccessToken],
	);

	const userEmail = (user as { email?: string } | null)?.email;

	return (
		<aside
			// React 19 `inert` when closed: blurs focused descendants + hides from
			// AT, which a bare aria-hidden could not do.
			inert={!open}
			className={cn(
				"shrink-0 overflow-hidden bg-background",
				fullScreen
					? "h-full w-full"
					: cn(
						"border-l transition-[width] duration-200 ease-out",
						open ? "w-[420px]" : "w-0",
					),
			)}
		>
			{/* Inner content is fixed-width on desktop so it doesn't visually
			    collapse during the width animation — the parent's overflow:hidden
			    reveals it gradually as the parent grows. Full-screen variant
			    fills its container instead. */}
			<div
				className={cn(
					"flex h-full flex-col",
					fullScreen ? "w-full" : "w-[420px]",
				)}
			>
				{!fullScreen && (
					<div className="flex items-center justify-between border-b px-3 py-2">
						<span className="text-sm font-semibold">Chat</span>
						<button
							type="button"
							onClick={onClose}
							className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
							title="Close"
							aria-label="Close PI agent"
						>
							<X className="h-4 w-4" />
						</button>
					</div>
				)}
				<div className="relative flex-1 overflow-hidden">
					{user ? (
						<MyJarvisRuntimeProvider getAccessToken={getToken} userEmail={userEmail}>
							<Thread />
						</MyJarvisRuntimeProvider>
					) : (
						<div className="absolute inset-x-4 top-4 rounded-md border bg-muted/30 p-4 text-sm text-muted-foreground">
							Sign in to use the PI agent.
						</div>
					)}
				</div>
			</div>
		</aside>
	);
}
